'use strict';

// ---------- Estado de la llamada + contestador automático ----------
//
// La versión de escritorio se enteraba de si "hay teléfono" mirando si
// apareció una tarjeta de sonido USB (phone-audio-detector.js, vía `pactl
// subscribe`). Acá la conexión WebUSB YA ES la señal de "hay teléfono": si
// se cortó el cable, el objeto Adb se entera solo (adb.disconnected).
//
// Lo que si hace falta seguir vigilando es el ESTADO DE LA LLAMADA
// (idle/ringing/offhook), para saber cuándo arrancar el flujo solo y cuándo
// contestar sola. Se sondea `dumpsys telephony.registry`, tal como hacía
// auto-responder.js, porque no hay una API de "avisame cuando cambie" al
// alcance de un simple shell.

import { shellTexto } from './adb-connection.js';
import { TinyEmitter } from './tiny-emitter.js';

const TECLAS = ['KEYCODE_HEADSETHOOK', 'KEYCODE_CALL'];
const POLL_MS = 1000;

function estadoDesdeVolcado(texto) {
  const m = texto.match(/mCallState=(\d)/);
  if (!m) return 'idle';
  if (m[1] === '1') return 'ringing';
  if (m[1] === '2') return 'offhook';
  return 'idle';
}

export class CallMonitor extends TinyEmitter {
  constructor({ adb, phoneBridge, log = () => {} }) {
    super();
    this.adb = adb;
    this.phoneBridge = phoneBridge;
    this.log = log;
    this.estado = null;
    this._timer = null;
    this.autoResponderActivo = false;
    this.autoResponderSegundos = 3;
    this._temporizadorContestar = null;
    this._atendiendo = false;
    this._ultimoTimbre = null;
  }

  configurarAutoResponder({ activo, segundos }) {
    if (activo !== undefined) this.autoResponderActivo = Boolean(activo);
    if (segundos !== undefined) this.autoResponderSegundos = Math.max(0, Number(segundos) || 0);
    if (!this.autoResponderActivo) this._cancelarContestar();
    return { activo: this.autoResponderActivo, segundos: this.autoResponderSegundos };
  }

  start() {
    if (this._timer) return;
    this._timer = setInterval(() => this._tick(), POLL_MS);
    this._tick();
  }

  stop() {
    clearInterval(this._timer);
    this._timer = null;
    this._cancelarContestar();
  }

  async _tick() {
    let texto;
    try {
      texto = await shellTexto(this.adb, ['dumpsys', 'telephony.registry']);
    } catch {
      return; // se cortó el USB o el shell falló: no rompemos el temporizador
    }
    const nuevo = estadoDesdeVolcado(texto);
    if (nuevo === this.estado) return;
    const anterior = this.estado;
    this.estado = nuevo;
    if (nuevo === 'ringing') this._ultimoTimbre = Date.now();
    this.emit('cambio', { anterior, actual: nuevo });

    if (nuevo === 'ringing') this._alSonar();
    else this._cancelarContestar();
  }

  _alSonar() {
    if (!this.autoResponderActivo || this._atendiendo || this._temporizadorContestar) return;
    this.log(`Contestador automático: atiendo en ${this.autoResponderSegundos} s…`);
    this._temporizadorContestar = setTimeout(async () => {
      this._temporizadorContestar = null;
      if (this.estado !== 'ringing') {
        this.log('La llamada dejó de sonar antes de que atendiera.');
        return;
      }
      this._atendiendo = true;
      await this._contestar();
      this._atendiendo = false;
    }, this.autoResponderSegundos * 1000);
  }

  _cancelarContestar() {
    if (this._temporizadorContestar) {
      clearTimeout(this._temporizadorContestar);
      this._temporizadorContestar = null;
    }
  }

  async _contestar() {
    for (const tecla of TECLAS) {
      try {
        await shellTexto(this.adb, ['input', 'keyevent', tecla]);
        await new Promise((r) => setTimeout(r, 1200));
        const texto = await shellTexto(this.adb, ['dumpsys', 'telephony.registry']);
        if (estadoDesdeVolcado(texto) !== 'ringing') {
          this.log(`Contestado con ${tecla}.`);
          return true;
        }
        this.log(`${tecla} no atendió, probando la siguiente…`);
      } catch (err) {
        this.log(`${tecla} falló: ${err.message}`);
      }
    }
    this.log('No se pudo contestar automáticamente.');
    return false;
  }
}
